import { ReactNode } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';
import { useBooleanState } from 'webrix/hooks';

import { useLayout } from 'lib/common/contexts/layout/LayoutContext';

import Badge, { TBadgeType } from 'lib/common/components/Badge/Badge';
import Icon from 'lib/common/components/Icon';
import Text from 'lib/common/components/Text';

import Tooltip from '../Tooltip';

import './checkbox.scss';

const SMALL_ICON_SIZE = 12;
const LARGE_ICON_SIZE = 16;

interface ICheckBox {
  checked?: boolean;
  initialValue?: boolean;
  onChange: (_: boolean) => any;
  label?: ReactNode | string;
  className?: string;
  disabled?: boolean;
  reverse?: boolean;
  id?: string;
  checkedIcon?: IconDefinition;
  tooltip?: string;
  tooltipDelay?: number;
  tooltipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
  small?: boolean;
  subtitle?: string;
  danger?: boolean;
  titleBadgeProps?: {
    label: string;
    type?: TBadgeType;
    className?: string;
  };
  titleSuffix?: ReactNode;
  ariaLabel?: string;
}

export default function Checkbox({
  checked: checkedProp,
  initialValue,
  onChange,
  label,
  className,
  disabled,
  id,
  checkedIcon,
  reverse,
  tooltip,
  tooltipDelay = 1000,
  tooltipPlacement = 'bottom',
  small,
  subtitle,
  danger,
  titleBadgeProps,
  titleSuffix = null,
  ariaLabel,
}: ICheckBox) {
  const { isSoftphone } = useLayout();
  const isSmall = small || isSoftphone;
  const { value: localChecked, toggle: toggleChecked } = useBooleanState(initialValue);
  const checked = checkedProp !== undefined ? checkedProp : localChecked;

  const handleChange = () => {
    const newChecked = !checked;

    onChange?.(newChecked);

    toggleChecked();
  };

  const stopEventPropagate = (e) => {
    e.stopPropagation();
  };

  const onButtonClick = (e) => {
    //@ts-ignore Hacky way to distinguish between a keyboard press and a button press. React unfortunately does not give us another way of doing this.
    if (e.target.tagName.toLowerCase() === 'button') {
      return handleChange();
    }

    stopEventPropagate(e);
  };

  const checkboxEl = (
    <button
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel || label?.toString()}
      className={cx(
        'checkbox',
        'no-styles-button',
        { 'checkbox--reverse': reverse, 'checkbox--disabled': disabled },
        className
      )}
      data-testid={id}
      onClick={onButtonClick}
    >
      <Text
        type="label"
        className={cx('checkbox__input', {
          'checkbox__input--danger': danger,
          'checkbox__input--checked': checked,
          'checkbox__input--small': isSmall,
          'checkbox__input--disabled': disabled,
        })}
        data-testid={`${id}__label`}
      >
        <input
          disabled={disabled}
          role="checkbox"
          data-testid={`${id}-${checkedIcon ? 'custom-icon' : 'check'}`}
          id={label ? `checkbox-${id}` : ''}
          onClick={stopEventPropagate}
          type="checkbox"
          checked={checked}
          onChange={disabled ? undefined : handleChange}
        />
        {checked && (
          <Icon
            size={isSmall ? SMALL_ICON_SIZE : LARGE_ICON_SIZE}
            icon={checkedIcon || faCheck}
            className="checkbox__tick"
            color="white"
          />
        )}
      </Text>
      <div className="checkbox__label" onClick={disabled ? undefined : handleChange}>
        {label && (
          <div className="checkbox__label__title-with-badge">
            <Text
              htmlFor={label ? `checkbox-${id}` : ''}
              disabled={disabled}
              className={cx('checkbox__label-text', { 'checkbox__label-text--disabled': disabled })}
            >
              {label}
            </Text>
            {titleBadgeProps && <Badge {...titleBadgeProps} />}
            {titleSuffix && <span className="checkbox__label__title-suffix">{titleSuffix}</span>}
          </div>
        )}
        {subtitle && (
          <Text className={cx('checkbox__subtitle', { 'checkbox__subtitle--disabled': disabled })} type="label">
            {subtitle}
          </Text>
        )}
      </div>
    </button>
  );

  return tooltip ? (
    <Tooltip enterDelay={tooltipDelay} title={tooltip} placement={tooltipPlacement} ariaHidden>
      {checkboxEl}
    </Tooltip>
  ) : (
    checkboxEl
  );
}
